import type { TranslationTree } from '../../types';

export const labeling: TranslationTree = {
  labeling: {
    page: {
      breadcrumb: 'Beschriftung',
      title: 'Werkzeugzustand-Beschriftung',
      subtitle:
        'Klassifizieren Sie jedes Kantenbild nach dem Werkzeugzustand, um das Predictive-Maintenance-Modell zu trainieren. Nutzen Sie die Tastatur für schnelles Arbeiten.',
    },
    shortcuts: {
      button: 'Tastenkürzel',
      title: 'Tastaturkürzel',
      labelOk: 'Gut (OK) beschriften',
      labelWorn: 'Verschlissen beschriften',
      classify: 'Mit KI klassifizieren',
      prevNext: 'Vorheriges / nächstes Bild',
      undo: 'Letzte Beschriftung rückgängig',
    },
    toolbar: {
      modeFocus: 'Fokus',
      modeGrid: 'Raster',
      modeDichotomous: 'Dichotomisch',
      statusAll: 'Alle',
      statusTodo: 'Ausstehend',
      statusDone: 'Erledigt',
      progress: '{{done}} / {{total}} beschriftet',
      autoAdvance: 'Auto-Weiter',
      modeLabel: 'Beschriftungsmodus',
      statusLabel: 'Statusfilter',
    },
    state: {
      ok: 'Gut (OK)',
      worn: 'Verschlissen',
      shortOk: 'OK',
      shortWorn: 'Verschlissen',
      unreadable: 'Bild unlesbar',
      shortUnreadable: 'Unlesbar',
    },
    panel: {
      stateHead: 'Werkzeugzustand',
      prev: 'Vorheriger',
      next: 'Nächster',
      undo: 'Rückgängig',
    },
    cnn: {
      button: 'Mit KI klassifizieren',
      loading: 'Klassifiziere…',
      suggestion: 'KI-Vorschlag: {{state}}',
      suggestionConfidence: 'KI-Vorschlag: {{state}} ({{confidence}} %)',
      accept: 'Akzeptieren',
      reject: 'Ablehnen',
      acceptAll: 'Alle KI akzeptieren',
      rejectAll: 'Alle ablehnen',
      classifyAll: 'Alle',
      pendingCount: '{{count}} ausstehend',
      noScore: 'kein Konfidenzwert',
      tag: 'KI',
      error: 'Klassifizierung fehlgeschlagen',
    },
    dev: {
      resetButton: 'DEV Zurücksetzen',
      resetTitle: 'Annotationen zurücksetzen',
      resetBody: 'Löscht alle Labels und KI-Vorschläge der aktuellen Sitzung. Nur für Entwicklung.',
      resetConfirm: 'Zurücksetzen',
      resetCancel: 'Abbrechen',
    },
    viewer: {
      imageAlt: 'Werkzeug-Inspektionsbild',
      zoomFit: 'Einpassen',
      zoomIn: 'Vergrößern',
      zoomOut: 'Verkleinern',
      capturedAt: 'Aufgenommen {{datetime}}',
      imageCount: 'Bild {{n}} von {{total}}',
    },
    grid: {
      selectedCount: '{{count}} ausgewählt',
      markAs: 'Markieren als:',
      deselect: 'Auswahl aufheben',
      bulkClassify: 'Mit KI klassifizieren',
      selectImage: 'Bild auswählen',
      openImage: 'Im Fokus-Modus öffnen',
    },
    legend: {
      ok: 'Gut',
      worn: 'Verschlissen',
      unreadable: 'Unlesbar',
    },
    complete: {
      banner: 'Alle Bilder in diesem Filter sind beschriftet. Gute Arbeit!',
    },
    selector: {
      title: 'Werkzeug zum Beschriften auswählen',
      subtitle:
        'Wählen Sie ein Werkzeug aus, um seine Beschriftungssitzung zu starten oder fortzusetzen.',
      searchPlaceholder: 'Nach Werkzeug oder Referenz suchen',
      filterAll: 'Alle',
      filterInProgress: 'In Bearbeitung',
      filterNotStarted: 'Nicht begonnen',
      statusNotStarted: 'Nicht begonnen',
      statusInProgress: 'In Bearbeitung',
      statusComplete: 'Abgeschlossen',
      traceCount: '{{count}} Spuren',
      labeledCount: '{{done}} / {{total}} beschriftet',
      noReference: 'Keine Referenz',
      startLabeling: 'Beschriftung starten',
      cancelSelection: 'Abbrechen',
      selectedTool: 'Ausgewählt: {{tool}}',
      changeTool: 'Werkzeug wechseln',
      emptyTitle: 'Keine Werkzeuge verfügbar',
      emptyHint: 'Für Ihr Konto wurden keine Werkzeuge gefunden.',
      errorTitle: 'Werkzeuge konnten nicht geladen werden',
      errorRetry: 'Erneut versuchen',
      loading: 'Werkzeuge werden geladen…',
      experiments: 'Experimente',
      toolsIn: 'Teile in',
      partCount: 'Teile in diesem Experiment',
      selectExperimentHint: 'Wählen Sie ein Experiment, um die Werkzeuge zu sehen.',
    },
    export: {
      button: 'Exportieren (Excel)',
      hint: 'Von allen Annotatoren beschriftete Daten exportieren: pro beschriftetem Werkzeug der letzte gute und der erste verschlissene Zustand (Teil und Zeitstempel).',
      inProgress: 'Export… {{done}} / {{total}}',
      empty: 'Kein beschriftetes Werkzeug zum Exportieren.',
      error: 'Export fehlgeschlagen. Bitte erneut versuchen.',
    },
    partPicker: {
      title: 'Teil auswählen',
      hint: 'Wählen Sie die Bildsequenz, an der Sie arbeiten möchten.',
      partLabel: 'Teil {{partId}}',
      imageCount: '{{count}} Bilder',
      fullyLabeled: 'Bereits beschriftet',
      empty: 'Kein Teil für diese Sitzung verfügbar.',
      allParts: 'Alle Teile',
      allPartsHint: 'Alle Teile, gemischte chronologische Reihenfolge.',
      triggerAll: 'Alle Teile',
      triggerSelected: 'Teil {{partId}}',
    },
    dichotomous: {
      compare: {
        title: 'Vergleich',
        progress: 'Vergleich {{n}} von ~{{total}}',
        question: 'Ist dieses Bild noch in Ordnung oder bereits verschlissen?',
        btnOk: 'Noch in Ordnung',
        btnWorn: 'Verschlissen',
        btnUnreadable: 'Bild unlesbar',
        rangeLabel: 'Suchfortschritt über die Sequenz',
        endpointFirst: 'Bestätigen Sie das erste Bild: Es sollte in Ordnung sein.',
        endpointLast: 'Bestätigen Sie das letzte Bild: Es sollte verschlissen sein.',
      },
      verify: {
        title: 'Überprüfung',
        progress: 'Überprüfung {{n}} von {{total}}',
        question: 'Ist dieses Bild noch in Ordnung oder bereits verschlissen?',
        intro:
          'Übergangspunkt gefunden. Beschriften Sie noch ein paar Bilder, um ihn zu bestätigen.',
      },
      conflict: {
        title: 'Unerwartetes Ergebnis',
        body: 'Bild {{position}} sollte „{{expected}}“ sein, wurde aber als „{{given}}“ beschriftet.',
        expected: 'Erwartet',
        given: 'Ihre Antwort',
        position: 'Bild {{index}} von {{total}}',
        actionRerun: 'Suche von hier neu starten',
        actionKeep: 'Diesen Übergangspunkt behalten',
        overwriteWarning:
          'Wenn Sie diesen Übergangspunkt behalten, wird die soeben vergebene Beschriftung beim Anwenden durch die Gruppenbeschriftung ersetzt.',
      },
      summary: {
        title: 'Bereit zum Anwenden',
        counts: '{{ok}} Bilder werden als „In Ordnung“, {{worn}} als „Verschlissen“ beschriftet.',
        confirm: 'Beschriftungen anwenden',
        cancel: 'Abbrechen',
        overwriteWarning: 'Bestehende Beschriftungen auf diesen Bildern werden ersetzt.',
        unreadableCount: '{{count}} unlesbare(s) Bild(er) werden als Unlesbar beschriftet.',
        restart: 'Suche neu starten',
      },
      degenerate: {
        allWornTitle: 'Keine intakten Bilder gefunden',
        allWornBody:
          'Das erste Bild ist bereits verschlissen: Dieses Teil zeigt in dieser Serie keine intakten Bilder.',
        allOkTitle: 'Keine verschlissenen Bilder gefunden',
        allOkBody:
          'Das letzte Bild ist noch in Ordnung: Dieses Teil zeigt in dieser Serie keinen Verschleiß.',
        allUnreadableTitle: 'Kein lesbares Bild',
        allUnreadableBody:
          'Alle Bilder dieser Serie sind unlesbar: Es gibt keinen Übergangspunkt zu suchen.',
        applyAll: 'Ganze Serie beschriften',
        fallbackManual: 'Manuell beschriften',
      },
      done: {
        title: 'Fertig',
        backToModes: 'Zurück zu den Beschriftungsmodi',
      },
    },
  },
};
